import { createContext, useContext, ReactNode, useMemo, useEffect, useLayoutEffect, useState } from "react";
import { BusinessDetails, setAuthToken, setBusinessDetails, apiClient, hasValidAuthentication, getAuthenticationStatus } from "@/api/client";
import { fetchCurrentUser } from "@/api/user";
import { CurrentUser } from "@/types/user";

interface AuthContextType {
  authToken: string;
  businessDetails: BusinessDetails | string | null;
  isAuthenticated: boolean;
  canMakeApiCalls: boolean;
  apiBaseUrl?: string;
  currentUser: CurrentUser | null;
  isLoadingUser: boolean;
  getAuthStatus: () => {
    hasAuthToken: boolean;
    hasBusinessDetails: boolean;
    isAuthenticated: boolean;
    missing: string[];
  };
}

interface AuthProviderProps {
  children: ReactNode;
  authToken?: string;
  businessDetails?: BusinessDetails | string | null;
  config?: {
    apiBaseUrl?: string;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ 
  children, 
  authToken = "", 
  businessDetails = null,
  config
}: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);
  const [resolvedBusinessDetails, setResolvedBusinessDetails] = useState<BusinessDetails | string | null>(businessDetails);

  useLayoutEffect(() => {
    // console.log('AuthProvider: Setting up auth', { authToken: !!authToken, businessDetails: !!businessDetails });
    
    if (config?.apiBaseUrl) {
      apiClient.defaults.baseURL = config.apiBaseUrl;
      if (config.apiBaseUrl.includes('ngrok')) {
        apiClient.defaults.headers['ngrok-skip-browser-warning'] = 'true';
      }
    }
    if (authToken) {
      setAuthToken(authToken);
      // console.log('AuthProvider: Token set');
    }
    if (businessDetails) {
      setResolvedBusinessDetails(businessDetails);
      setBusinessDetails(businessDetails);
      // console.log('AuthProvider: Business details set');
    }
    
    // console.log('AuthProvider: Headers:', apiClient.defaults.headers.common);
  }, [authToken, businessDetails, config]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      if (!authToken) {
        setCurrentUser(null);
        return;
      }

      try {
        setIsLoadingUser(true);
        // console.log('AuthProvider: Fetching current user...');
        const response = await fetchCurrentUser(config?.apiBaseUrl);
        
        if (response.success && response.data) {
          setCurrentUser(response.data);
          if (!resolvedBusinessDetails) {
            const derivedBusinessDetails = response.data?.user_details?.user_full_hotel_agency_details;
            if (derivedBusinessDetails?.user_id && derivedBusinessDetails?.org_name) {
              const derived = {
                user_id: String(derivedBusinessDetails.user_id),
                org_name: derivedBusinessDetails.org_name,
              };
              setResolvedBusinessDetails(derived);
              setBusinessDetails(derived);
            }
          }
          // console.log('AuthProvider: Current user loaded:', response.data);
        }
      } catch (error) {
        // console.error('AuthProvider: Failed to fetch current user:', error);
        setCurrentUser(null);
      } finally {
        setIsLoadingUser(false);
      }
    };

    loadCurrentUser();
  }, [authToken, resolvedBusinessDetails, config?.apiBaseUrl]);

  const contextValue = useMemo(
    () => ({
      authToken,
      businessDetails: resolvedBusinessDetails,
      isAuthenticated: !!authToken && !!resolvedBusinessDetails,
      canMakeApiCalls: hasValidAuthentication(),
      apiBaseUrl: config?.apiBaseUrl,
      currentUser,
      isLoadingUser,
      getAuthStatus: () => getAuthenticationStatus(),
    }),
    [authToken, resolvedBusinessDetails, config?.apiBaseUrl, currentUser, isLoadingUser]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

const defaultAuthContext: AuthContextType = {
  authToken: "",
  businessDetails: null,
  isAuthenticated: false,
  canMakeApiCalls: false,
  apiBaseUrl: undefined,
  currentUser: null,
  isLoadingUser: false,
  getAuthStatus: () => ({
    hasAuthToken: false,
    hasBusinessDetails: false,
    isAuthenticated: false,
    missing: ['auth token', 'business details']
  })
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    return defaultAuthContext;
  }
  return context;
}
